import { Component, Input, forwardRef, OnInit, OnDestroy, SimpleChanges, OnChanges, ViewChild, Optional } from '@angular/core';

import { filter, Subscription } from 'rxjs';

import { AbstractFormFieldComponent } from '../../../../Common/PageBuilder/components/FormField/abstract-form-field.component';
import { BriefingDateFieldModel } from '../../models/briefing-date-field.model';
import { BlockDisplayTypeEnum } from '../../../../Common/PageBuilder/enums/block-display-type.enum';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateDisplayElementModel } from '../../../../Common/PageBuilder/models/date-display-element.model';
import { BriefingDateRangeTypeEnum } from '../../enums/briefing-date-range-type.enum';
import { getMonitoringDateQueryInputTopicOptions } from '../../../Shared/models/monitoring-date-query-input-options';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { BriefingsService } from '../../services/briefings.service';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';


@Component({
    selector: 'agpr-briefing-date-field',
    templateUrl: './briefing-date-field.component.html',
    styleUrls: ['./briefing-date-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => BriefingDateFieldComponent)
        }
    ],
    standalone: false
})
export class BriefingDateFieldComponent extends AbstractFormFieldComponent implements OnInit, OnChanges, OnDestroy {
    @Input() declare field: BriefingDateFieldModel;
    @Input() declare value: number;

    @ViewChild('startCalendar') startCalendar: any;

    public _value: Date;
    public readonly dateRangeOptions = getMonitoringDateQueryInputTopicOptions();

    public _dateQuery: DateQueryModel = this.dateRangeOptions[0].value.query;

    public modeSendDate: number = 1;
    public modeSpecificDate: number = 2;
    public modeDateRange: number = 3;

    public readonly dateRangeTypeEnum = BriefingDateRangeTypeEnum;

    showViewInBrowser: boolean = true;

    public mode: number = this.modeSendDate;

    // "Date Range" sub-option: a specific selection vs. one derived from the Briefing's
    // recurrence schedule. Defaults to Specific Date Range.
    public selectedDateRangeType: BriefingDateRangeTypeEnum = BriefingDateRangeTypeEnum.SPECIFIC;

    // Whether the Briefing publishes on a recurring schedule; gates the "Based on
    // Recurrence Date Range" option. Always false outside a Briefing context (e.g. Alerts).
    public isRecurring: boolean = false;

    // The user's preferred timezone, stamped onto relative date-range queries so they
    // resolve and render in that timezone (and so it's persisted to the backend).
    private userTimeZone: string = null;
    private preferencesSubscription: Subscription;
    private briefingSubscription: Subscription;

    constructor(
        private currentUserStateService: CurrentUserStateService,
        @Optional() private briefingsService: BriefingsService
    ) {
        super();
    }

    /**
     * Opens the calendar popup
     */
    openCalendar(event, calendar): void {
        calendar.hideOverlay();
        calendar.showOverlay();
        event.stopPropagation();
    }

    ngOnInit(): void {
        const displayElement = this.block.displayElement as DateDisplayElementModel;
        this.showViewInBrowser = displayElement.displayType !== BlockDisplayTypeEnum.ONLINE;

        // Track whether the Briefing is recurring. A BehaviorSubject emits synchronously on
        // subscribe, so `isRecurring` is set before the initial mode is derived below.
        let initialized = false;
        this.briefingSubscription = this.briefingsService?.getFocusedResource()
            .subscribe(briefing => {
                this.isRecurring = !!briefing?.sendAt?.recurring;
                if (!initialized) {
                    initialized = true;
                    this.initializeMode(displayElement);
                } else if (!this.isRecurring && this.selectedDateRangeType === this.dateRangeTypeEnum.RECURRENCE) {
                    // Scheduling changed away from recurring while this block is set to
                    // "Based on Recurrence Date Range": fall back to the default range.
                    this.resetRecurrenceToDefaultRange(displayElement);
                }
            });

        if (!initialized) {
            // No Briefing context (e.g. Alerts) or the service isn't provided.
            this.initializeMode(displayElement);
        }

        this.preferencesSubscription = this.currentUserStateService.getPreferences()
            .pipe(filter(preferences => !!preferences))
            .subscribe(preferences => {
                this.userTimeZone = preferences.timeZone;
                // Apply to an already-selected relative range so the timezone is persisted
                // even if the user doesn't re-open the picker.
                if (this.mode === this.modeDateRange && this.selectedDateRangeType === this.dateRangeTypeEnum.SPECIFIC) {
                    this.applyUserTimezone(this._dateQuery);
                }
            });
    }

    ngOnDestroy(): void {
        this.preferencesSubscription?.unsubscribe();
        this.briefingSubscription?.unsubscribe();
    }

    /**
     * Derives the initial radio selection from the persisted block. A block saved as
     * "Based on Recurrence Date Range" reverts to the default range if the Briefing is no longer
     * recurring; otherwise a typed dateQuery means "Specific Date Range".
     */
    private initializeMode(displayElement: DateDisplayElementModel): void {
        if (displayElement.dateRangeType === this.dateRangeTypeEnum.RECURRENCE) {
            if (this.isRecurring) {
                this.mode = this.modeDateRange;
                this.selectedDateRangeType = this.dateRangeTypeEnum.RECURRENCE;
                if (displayElement.dateQuery?.type) {
                    this._dateQuery = displayElement.dateQuery;
                }
            } else {
                this.resetRecurrenceToDefaultRange(displayElement);
            }
        } else if (displayElement.dateQuery?.type) {
            // Require a real `type`; a stray empty/typeless dateQuery must not force date-range mode.
            this.mode = this.modeDateRange;
            this.selectedDateRangeType = this.dateRangeTypeEnum.SPECIFIC;
            this._dateQuery = displayElement.dateQuery;
        } else {
            this.mode = this._value ? this.modeSpecificDate : this.modeSendDate;
        }
    }

    private resetRecurrenceToDefaultRange(displayElement: DateDisplayElementModel): void {
        this.mode = this.modeDateRange;
        this.selectedDateRangeType = this.dateRangeTypeEnum.SPECIFIC;
        displayElement.dateRangeType = BriefingDateRangeTypeEnum.SPECIFIC;
        displayElement.dateQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);
        displayElement.dateQuery.timezone = this.userTimeZone;
        this.changes.emit(null);
    }

    /**
     * Ensures a relative or absolute date-range query carries a timezone: a timezone already on
     * the query (saved block or dropdown selection) is kept as-is, otherwise the user's preferred
     * timezone is applied. Absolute ranges need it too — their endpoints are end-of-day instants
     * in the user's timezone, and without it the render resolves the calendar day in the server
     * timezone, rolling the end into the next day. Other query types carry none.
     */
    private applyUserTimezone(dateQuery: DateQueryModel): void {
        if (!dateQuery) {
            return;
        }
        if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE || dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            if (!dateQuery.timezone) {
                dateQuery.timezone = this.userTimeZone;
            }
        } else {
            dateQuery.timezone = undefined;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('value')) {
            this._value = this.value ? new Date(this.value) : null;
        }
    }

    public modeChanged(mode: number): void {
        const displayElement = this.block.displayElement as DateDisplayElementModel;
        if (mode === this.modeDateRange) {
            // "Based on Recurrence Date Range" is only valid while the Briefing is recurring;
            // otherwise default to Specific Date Range.
            if (this.selectedDateRangeType === this.dateRangeTypeEnum.RECURRENCE && !this.isRecurring) {
                this.selectedDateRangeType = this.dateRangeTypeEnum.SPECIFIC;
            }
            this.dateRangeTypeChanged(this.selectedDateRangeType);
        } else {
            displayElement.dateQuery = undefined;
            displayElement.dateRangeType = undefined;
            this.changes.emit(mode === this.modeSendDate ? null : this._value);
        }
    }

    public dateRangeTypeChanged(dateRangeType: BriefingDateRangeTypeEnum): void {
        this.selectedDateRangeType = dateRangeType;
        const displayElement = this.block.displayElement as DateDisplayElementModel;
        if (dateRangeType === this.dateRangeTypeEnum.RECURRENCE) {
            // The range is resolved from the recurrence schedule at send time, so no explicit
            // query is persisted with the block.
            displayElement.dateRangeType = this.dateRangeTypeEnum.RECURRENCE;
            displayElement.dateQuery = undefined;
        } else {
            displayElement.dateRangeType = this.dateRangeTypeEnum.SPECIFIC;
            this.applyUserTimezone(this._dateQuery);
            displayElement.dateQuery = this._dateQuery;
        }
        this.changes.emit(null);
    }

    public dateRangeChanged(dateQuery: DateQueryModel): void {
        // Keep the timezone picked in the dropdown when only the range preset changes.
        if (dateQuery?.type === DateQueryTypeEnum.RELATIVE_TIMEZONE && !dateQuery.timezone && this._dateQuery?.timezone) {
            dateQuery.timezone = this._dateQuery.timezone;
        }
        this.applyUserTimezone(dateQuery);
        this._dateQuery = dateQuery;
        (this.block.displayElement as DateDisplayElementModel).dateQuery = dateQuery;
        this.changes.emit(null);
    }

    public dateSelected(chosenDate: string): void {
        this.changes.emit(+chosenDate);
    }

    public blockChanged(): void {
        // if 'this._value' is unset/null, the '+' will make it coerce to '0', which is a valid date
        // though a value of unset or null should indicate an invalid or not present date
        if (!this._value) {
            this.changes.emit(this._value);

        } else {
            this.changes.emit(+this._value);
        }
    }
}
